import { useState } from 'react';
import { Collapsible, Flex, HStack, IconButton } from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { LuChevronLeft, LuChevronsUpDown } from 'react-icons/lu';
import { Link as RouterLink } from 'react-router-dom';
import { CONTENT_PX, SUBHEADER_H } from '../layoutTokens';
import { PageActionButton, PageActionMenu } from './PageActions';
import { splitActions } from './splitActions';
import { PageBreadcrumbs } from './PageBreadcrumbs';
import { sectionPath } from './navConfig';
import type { PageBack, PageChrome, PageCrumb } from '@/contexts/PageChromeContext';
import type { WorkspaceNav } from './useWorkspaceNav';

interface PageSubheaderProps {
    nav: WorkspaceNav;
    chrome: PageChrome;
    compact: boolean;
}

export function PageSubheader({ nav, chrome, compact }: PageSubheaderProps) {
    const intl = useIntl();
    const [metaOpen, setMetaOpen] = useState(false);
    const { sections, activeSectionId, prefsPanes, activePrefsPaneId, inPreferences } = nav;

    const workspace = nav.scoped ? nav.workspace : null;

    const activeSection = sections.find((section) => section.id === activeSectionId);
    const sectionLabel = activeSection ? intl.formatMessage(activeSection.label) : undefined;
    const activePane = prefsPanes.find((pane) => pane.id === activePrefsPaneId);

    const crumbs: PageCrumb[] = [];
    if (workspace) {
        if (sectionLabel && activeSection) {
            crumbs.push({
                label: sectionLabel,
                to: sectionPath(workspace.stage, workspace.id, activeSection),
            });
        }

        // A pane promoted to its own top-level row is already crumb 2; adding it
        // again would read "Members ▸ Members".
        const paneIsCrumb2 = activeSection?.path === `preferences/${activePane?.path}`;
        if (inPreferences && activePane && !paneIsCrumb2) {
            crumbs.push({ label: intl.formatMessage(activePane.label) });
        }
    }
    if (chrome.trailingCrumbs?.length) crumbs.push(...chrome.trailingCrumbs);

    const title = chrome.title ?? sectionLabel ?? workspace?.name;
    const parentCrumb = crumbs.length > 1 ? crumbs[crumbs.length - 2] : undefined;
    const settingsBack: PageBack | undefined = workspace && activeSection && inPreferences && activePane
        ? {
            to: sectionPath(workspace.stage, workspace.id, activeSection),
            label: intl.formatMessage({
                id: 'e4b96a31-7c25-4d80-a1f7-5e2c9b638d04',
                defaultMessage: 'Back to settings',
            }),
        }
        : undefined;
    const back = chrome.back
        ?? settingsBack
        ?? (parentCrumb?.to ? { to: parentCrumb.to, label: parentCrumb.label } : undefined);
    const backLabel = back?.label ?? intl.formatMessage({
        id: 'f1e6f8b2-9d3a-4c5e-8b7f-6a2d0e4c9b18',
        defaultMessage: 'Back',
    });

    const actions = chrome.actions ?? [];
    const hasPrimary = Boolean(chrome.primaryAction);
    const { visible, overflow } = splitActions(actions, { hasPrimary, compact });
    const hasActions = actions.length > 0 || hasPrimary;
    const metaLabel = intl.formatMessage({
        id: '0c4d8e17-2b6a-4f93-a5c1-7e9b3d0f6a28',
        defaultMessage: 'Show page details',
    });

    if (!workspace && !title) return null;

    return (
        <Collapsible.Root
            open={metaOpen}
            onOpenChange={({ open }) => setMetaOpen(open)}
            disabled={!chrome.meta}
            position="sticky"
            top={0}
            zIndex={4}
            flexShrink={0}
            bg="bg.canvas"
            borderBottomWidth="1px"
            borderColor="border"
        >
            <Flex h={SUBHEADER_H} align="center" justify="space-between" gap="3" px={CONTENT_PX}>
                <HStack gap="1" minW={0}>
                    {back && (
                        <IconButton
                            asChild
                            variant="ghost"
                            size="xs"
                            aria-label={backLabel}
                            color="fg.muted"
                            flexShrink={0}
                        >
                            <RouterLink to={back.to}>
                                <LuChevronLeft />
                            </RouterLink>
                        </IconButton>
                    )}
                    <PageBreadcrumbs
                        workspace={workspace}
                        crumbs={crumbs}
                        current={chrome.title ?? (crumbs.length ? undefined : title)}
                        compact={compact}
                    />
                    {chrome.meta && (
                        <Collapsible.Trigger asChild>
                            <IconButton
                                variant="ghost"
                                size="2xs"
                                aria-label={metaLabel}
                                color="fg.muted"
                                flexShrink={0}
                                borderWidth="1px"
                                borderColor={metaOpen ? 'border.emphasized' : 'transparent'}
                            >
                                <LuChevronsUpDown />
                            </IconButton>
                        </Collapsible.Trigger>
                    )}
                </HStack>

                {hasActions && (
                    <HStack gap="2" flexShrink={0}>
                        {visible.map((action) => (
                            <PageActionButton key={action.id} action={action} />
                        ))}
                        <PageActionMenu actions={overflow} />
                        {chrome.primaryAction}
                    </HStack>
                )}
            </Flex>

            {chrome.meta && (
                <Collapsible.Content>
                    <HStack
                        gap="2.5"
                        px={CONTENT_PX}
                        pb="3"
                        fontSize="sm"
                        color="fg.muted"
                        flexWrap="wrap"
                    >
                        {chrome.meta}
                    </HStack>
                </Collapsible.Content>
            )}
        </Collapsible.Root>
    );
}
